import { consola } from 'consola'
// useCartProduct.ts

import { computed, unref } from 'vue';
import { useCartStore } from '@entities/order';

/**
 * Универсальный хук для работы с продуктом в корзине по ключу id + branch + addons + excludes
 * Все параметры должны быть реактивными: ref или computed!
 */
export function useCartProduct({ product, branchId, addons = [], excludes = [] }) {
  const cartStore = useCartStore();

  // Генерация ключа для уникальности позиции (id, branch, addons, excludes)
  const getKey = () => {
    const p = unref(product);
    const b = unref(branchId);
    const a = (unref(addons) || []).map(String).sort();
    const e = (unref(excludes) || []).map(String).sort();
    return [
      String(p.id),
      String(b),
      JSON.stringify(a),
      JSON.stringify(e),
    ].join('_');
  };

  // DEBUG: входные параметры всегда!
  consola.debug('[useCartProduct] product:', product);
  consola.debug('[useCartProduct] branchId:', branchId);
  consola.debug('[useCartProduct] addons:', addons);
  consola.debug('[useCartProduct] excludes:', excludes);

  // Поиск позиции в корзине
  const cartItem = computed(() => {
    const p = unref(product);
    const b = unref(branchId);
    const a = (unref(addons) || []).map(String).sort();
    const e = (unref(excludes) || []).map(String).sort();

    // consola.debug('[useCartProduct] cartItem computed', {
    //   id: p.id,
    //   branchId: b,
    //   addons: a,
    //   excludes: e,
    //   found,
    //   cart: cartStore.cart,
    // });
    return cartStore.cart.find(
      (item) =>
        String(item.id) === String(p.id) &&
        String(item.branch_id) === String(b) &&
        JSON.stringify((item.addons || []).map(String).sort()) === JSON.stringify(a) &&
        JSON.stringify((item.excludes || []).map(String).sort()) === JSON.stringify(e),
    );
  });

  const quantity = computed(() => cartItem.value?.quantity ?? 0);
  const inCart = computed(() => quantity.value > 0);

  // Добавить товар (с выбранными добавками и отбавками)
  const addToCart = async (extraPayload = {}) => {
    const p = unref(product);
    const b = unref(branchId);
    const a = unref(addons);
    const e = unref(excludes);

    consola.debug('[useCartProduct:addToCart] payload:', {
      id: p.id,
      name: p.name,
      price: p.price,
      img: p.img,
      category_id: p.category_id, // ← Передаем category_id для промокодов
      addons: a,
      excludes: e,
      ...extraPayload,
    }, 'branchId:', b);
    await cartStore.addToCart(
      {
        id: p.id,
        name: p.name,
        price: p.price,
        img: p.img,
        category_id: p.category_id, // ← Передаем category_id для промокодов
        addons: a,
        excludes: e,
        ...extraPayload,
      },
      b,
    );
  };

  /**
   * Увеличить количество товара на 1
   *
   * ✅ ВАЖНО: Передается абсолютное значение (currentQuantity + 1),
   * но CartStore внутри правильно вычисляет дельту и использует
   * CartQuantityService для синхронизации с сервером
   */
  const increment = async () => {
    const b = unref(branchId);
    const a = unref(addons);
    const e = unref(excludes);

    if (cartItem.value) {
      consola.debug('[useCartProduct:increment] cartItem:', cartItem.value, '->', cartItem.value.quantity + 1);
      // Передаем новое абсолютное значение - store сам разберется с синхронизацией
      await cartStore.updateQuantity(
        cartItem.value.id,
        cartItem.value.quantity + 1,
        b,
        a,
        e,
      );
    } else {
      consola.debug('[useCartProduct:increment] no cartItem, call addToCart');
      await addToCart();
    }
  };

  /**
   * Уменьшить количество товара на 1
   *
   * ✅ ВАЖНО: Передается абсолютное значение (currentQuantity - 1),
   * но CartStore внутри правильно вычисляет дельту и использует
   * CartQuantityService для синхронизации с сервером
   */
  const decrement = async () => {
    const b = unref(branchId);
    const a = unref(addons);
    const e = unref(excludes);

    if (cartItem.value) {
      const nextQuantity = cartItem.value.quantity - 1;
      if (nextQuantity <= 0) {
        consola.debug('[useCartProduct:decrement] quantity will be 0, remove');
        await removeFromCart();
      } else {
        consola.debug('[useCartProduct:decrement] cartItem:', cartItem.value, '->', nextQuantity);
        // Передаем новое абсолютное значение - store сам разберется с синхронизацией
        await cartStore.updateQuantity(
          cartItem.value.id,
          nextQuantity,
          b,
          a,
          e,
        );
      }
    }
  };

  // Удалить товар из корзины полностью
  const removeFromCart = async () => {
    const p = unref(product);
    const b = unref(branchId);
    const a = unref(addons);
    const e = unref(excludes);

    consola.debug('[useCartProduct:removeFromCart] id:', p.id, 'branchId:', b, 'addons:', a, 'excludes:', e);
    await cartStore.removeFromCart(p.id, b, a, e);
  };

  return {
    cartItem,
    quantity,
    inCart,
    addToCart,
    increment,
    decrement,
    removeFromCart,
    getKey,
  };
}


